/**
 * 랜딩(WS-00 워크스페이스 홈) 축 카드 선언 — `WORKSPACE_NAV` 파생.
 *
 * 축명·설명·아이콘·대표 route·랜딩 표시값(태그·리드·칩)은 전부 workspaceNav.ts 가 소유한다(075 에서
 * 이중 선언 통합). 이 파일은 그 배열을 카드 형태로 옮기고, GNB 에는 없는 로드맵 항목(면접 준비 — 준비중)만
 * 오버레이로 덧붙인다. 새 축은 여기가 아니라 WORKSPACE_NAV 에 1행 추가한다.
 *
 * 샘플 데이터는 landingSamples.ts 로 분리했다(서버 조회 없음 — 034 FR-012 유지).
 *
 * `step` 은 카드 헤더 밴드 색(tokens.css `--step-1~5` 단계 스케일)의 순번 전용이다 — GNB 순서 그대로 1~5 를
 * 부여한다. 스케일이 5단이라 6번째부터는 마지막 색(step-5)을 재사용한다(구분이 필요해지면 스케일 확장).
 * 면접 준비(soon)는 step 없음 → 중성 헤더.
 */
import {
  IPSI_CARD_ROUTE,
  WORKSPACE_NAV,
  type WorkspaceLandingArt,
  type WorkspaceNavIcon,
  type WorkspaceNavKey,
} from './workspaceNav';

/** 축 식별자 — GNB 그룹 key + 랜딩 전용 로드맵 항목. */
export type LandingAxisKey = WorkspaceNavKey | 'interview';

/** 축 아이콘 키 — LandingAxisCard 가 lucide-vue-next 컴포넌트로 매핑한다. */
export type LandingAxisIcon = WorkspaceNavIcon | 'graduation-cap';

/** 헤더 밴드 순번 — `--step-{n}` 토큰 인덱스(1~3 흰 글자, 4~5 `--step-on-dark` 글자). */
export type LandingAxisStep = 1 | 2 | 3 | 4 | 5;

const AXIS_STEPS: readonly LandingAxisStep[] = [1, 2, 3, 4, 5];

interface LandingAxisBase {
  key: LandingAxisKey;
  /** 축명(= GNB 대분류 라벨). */
  name: string;
  /** 한 줄 설명(= GNB summary). */
  one: string;
  /** 태그 — 헤더 밴드 칩 텍스트(soon 카드는 "준비중"이 대신 찍힌다). */
  tag: string;
  icon: LandingAxisIcon;
  /** 리드 카드(내 입시카드=true — 전폭 배치). */
  lead?: true;
  /** 하위 기능 불릿 — landing.sub 가 없으면 GNB 하위 항목 라벨을 그대로 쓴다. */
  sub?: string[];
  /** 헤더 밴드 색 순번(open 축만). 없으면 중성 헤더. */
  step?: LandingAxisStep;
  /** 서비스 카드 2줄 소개(`\n` 줄바꿈) — WORKSPACE_NAV.landing.blurb 그대로. */
  blurb: string;
  /** 서비스 카드 일러스트 키 — public/assets/landing/art-{art}.webp. */
  art: WorkspaceLandingArt;
}

/**
 * 축 카드 선언 — 불변식(034 data-model §1):
 *  - `status='open'`  ⇒ `route` 필수(클릭 시 이동).
 *  - `status='soon'`  ⇒ `route` 없음("준비중" 표기 + 무동작).
 * 판별 유니언으로 타입 수준에서 강제한다.
 */
export type LandingAxis =
  | (LandingAxisBase & { status: 'open'; route: string })
  | (LandingAxisBase & { status: 'soon'; route?: never });

/** 축 카드·샘플 CTA 가 페이지로 올리는 이동 대상 — 게이트 모달의 대상명·복귀 route. */
export interface LandingGoTarget {
  /** 모달 본문에 굵게 찍히는 기능명. */
  name: string;
  route: string;
}

/** 면접 준비(WS-10) — 미오픈 로드맵. GNB 에는 없고 랜딩에서만 준비중으로 보인다. */
const INTERVIEW_SOON: LandingAxis = {
  key: 'interview',
  icon: 'graduation-cap',
  name: '면접 준비',
  one: '면접 기본기 가이드와 예상 질문 연습으로 실전을 준비합니다.',
  tag: '가이드·연습',
  sub: ['면접 기본기 가이드', '예상 질문 연습'],
  blurb: '면접 기출부터\n예상 질문까지',
  art: 'interview',
  status: 'soon',
};

/** 축 카드 — GNB 5그룹(전부 open, step 1~5) + 면접 준비(soon). 순서는 GNB 와 같다. */
export const LANDING_AXES: LandingAxis[] = [
  ...WORKSPACE_NAV.map((g, i): LandingAxis => {
    // 스케일(5단)을 넘는 축은 마지막 단계 색을 재사용 — 축 추가가 랜딩 렌더를 막지 않게 한다.
    const step = AXIS_STEPS[Math.min(i, AXIS_STEPS.length - 1)] ?? 5;
    return {
      key: g.key,
      name: g.label,
      one: g.summary,
      tag: g.landing.tag,
      icon: g.icon,
      lead: g.landing.lead,
      sub: g.landing.sub ?? g.items.map((item) => item.label),
      blurb: g.landing.blurb,
      art: g.landing.art,
      step,
      status: 'open',
      route: g.route,
    };
  }),
  INTERVIEW_SOON,
];

/**
 * [시작하기] 비로그인 복귀지 — 로그인 시점에는 성적 보유 여부를 알 수 없으므로 허브(입시카드) 고정.
 * 로그인 회원의 실제 이동 대상은 성적 보유 여부로 갈린다 — AnalysisHomePage 의 분기 참조(035 FR-022).
 */
export const LANDING_START_ROUTE = IPSI_CARD_ROUTE;
